import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { createActionReporter } from "../../../diagnostics";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { THROW_ON_ERROR } from "shared/dialogSubmit";

/** Sortable columns of the two mapping tables ("name" = Client / Partner). */
export type DomainMappingSortKey = "domain" | "name";

/** Both columns are text — every sort starts A→Z. */
const DESC_FIRST: ReadonlySet<DomainMappingSortKey> = new Set();

export interface EmailDomain {
  id: number;
  clientId?: string;
  partnerId?: string;
  domain: string;
  verified?: boolean;
}
interface NameOption {
  id: string;
  name: string;
}

/**
 * View store for the "Domain Mappings" admin view — maps corporate email domains
 * to clients / partners so meetings auto-resolve their parties by attendee
 * domain. Reads the `client-email-domains` / `partner-email-domains` services and
 * the `clients` / `partners` services (for the pickers). Registered as
 * `domainMappingsStore` (distinct from any ServiceStore property).
 */
class DomainMappingsStore {
  rootStore: RootStore;
  loading = false;
  /** `client-email-domains` failed to load — the Client table must not say
   *  "No domains mapped yet". */
  clientDomainsError: string | null = null;
  /** `partner-email-domains` failed to load. */
  partnerDomainsError: string | null = null;
  /** The client/partner lists behind the pickers + name column failed. Not a
   *  blocking error: mapped rows still render (names fall back to ids), but the
   *  pickers would be silently empty, so it is said out loud. */
  lookupError: string | null = null;

  /** One header search box narrows BOTH tables (domain or client/partner name). */
  searchQuery = "";

  // Per-table column sort (client-side; see tableSort.ts).
  clientSortKey: DomainMappingSortKey = "domain";
  clientSortDir: SortDir = "asc";
  partnerSortKey: DomainMappingSortKey = "domain";
  partnerSortDir: SortDir = "asc";

  newClientId = "";
  newClientDomain = "";
  newPartnerId = "";
  newPartnerDomain = "";
  savingClient = false;
  savingPartner = false;
  formError: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private svc(path: string, fallback?: unknown): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore(path) ?? fallback) as unknown as ServiceStoreType | null) ?? null
    );
  }
  private get clientDomainsService() {
    return this.svc("client-email-domains", this.rootStore.clientEmailDomainsStore);
  }
  private get partnerDomainsService() {
    return this.svc("partner-email-domains", this.rootStore.partnerEmailDomainsStore);
  }
  private get clientsService() {
    return this.svc("clients", this.rootStore.clientsStore);
  }
  private get partnersService() {
    return this.svc("partners", this.rootStore.partnersStore);
  }

  get clientDomains(): EmailDomain[] {
    return (this.clientDomainsService?.entities ?? []) as unknown as EmailDomain[];
  }
  get partnerDomains(): EmailDomain[] {
    return (this.partnerDomainsService?.entities ?? []) as unknown as EmailDomain[];
  }
  get clients(): NameOption[] {
    return (this.clientsService?.entities ?? []) as unknown as NameOption[];
  }
  get partners(): NameOption[] {
    return (this.partnersService?.entities ?? []) as unknown as NameOption[];
  }

  clientName(id?: string): string {
    return this.clients.find((c) => c.id === id)?.name || id || "-";
  }
  partnerName(id?: string): string {
    return this.partners.find((p) => p.id === id)?.name || id || "-";
  }

  // ---- Search + column sort ------------------------------------------------

  private matchesSearch(row: EmailDomain, name: string): boolean {
    const q = this.searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      row.domain?.toLowerCase().includes(q) || name.toLowerCase().includes(q)
    );
  }

  get filteredClientDomains(): EmailDomain[] {
    return this.clientDomains.filter((d) =>
      this.matchesSearch(d, this.clientName(d.clientId))
    );
  }

  get filteredPartnerDomains(): EmailDomain[] {
    return this.partnerDomains.filter((d) =>
      this.matchesSearch(d, this.partnerName(d.partnerId))
    );
  }

  private sorted(
    rows: EmailDomain[],
    nameOf: (d: EmailDomain) => string,
    key: DomainMappingSortKey,
    dir: SortDir
  ): EmailDomain[] {
    const valueOf = (d: EmailDomain): SortValue =>
      key === "domain" ? textValue(d.domain) : textValue(nameOf(d));
    return sortRows(rows, valueOf, dir);
  }

  /** Client-domains table rows: search filter, then the active column sort. */
  get sortedClientDomains(): EmailDomain[] {
    return this.sorted(
      this.filteredClientDomains,
      (d) => this.clientName(d.clientId),
      this.clientSortKey,
      this.clientSortDir
    );
  }

  /** Partner-domains table rows: search filter, then the active column sort. */
  get sortedPartnerDomains(): EmailDomain[] {
    return this.sorted(
      this.filteredPartnerDomains,
      (d) => this.partnerName(d.partnerId),
      this.partnerSortKey,
      this.partnerSortDir
    );
  }

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  /** Header click on the client-domains table. */
  setClientSort(key: DomainMappingSortKey) {
    const next = nextSort(this.clientSortKey, this.clientSortDir, key, DESC_FIRST);
    this.clientSortKey = next.key;
    this.clientSortDir = next.dir;
  }

  /** Header click on the partner-domains table. */
  setPartnerSort(key: DomainMappingSortKey) {
    const next = nextSort(this.partnerSortKey, this.partnerSortDir, key, DESC_FIRST);
    this.partnerSortKey = next.key;
    this.partnerSortDir = next.dir;
  }

  setField(field: "newClientId" | "newClientDomain" | "newPartnerId" | "newPartnerDomain", value: string) {
    this[field] = value;
  }

  async load() {
    const ended = sessionMark(this.rootStore);
    this.loading = true;
    this.clientDomainsError = null;
    this.partnerDomainsError = null;
    this.lookupError = null;
    // findOrThrow on every leg: the library ServiceStore resolves null on a
    // failed find, which rendered "No domains mapped yet". allSettled so one
    // failed table does not blank the other.
    const [clientDomains, partnerDomains, clients, partners] = await Promise.allSettled([
      findOrThrow(this.clientDomainsService, { query: { $limit: 500, $sort: { domain: 1 } } }),
      findOrThrow(this.partnerDomainsService, { query: { $limit: 500, $sort: { domain: 1 } } }),
      findOrThrow(this.clientsService, { query: { $limit: 500 } }),
      findOrThrow(this.partnersService, { query: { $limit: 500, $sort: { name: 1 } } }),
    ]);
    // allSettled waits for every leg: one that failed before a sign-out is the
    // previous person's failure, and reset() has already cleared `loading`.
    if (ended()) return;
    runInAction(() => {
      if (clientDomains.status === "rejected" && !isSessionEnded(clientDomains.reason)) {
        this.clientDomainsError = loadErrorMessage(
          clientDomains.reason,
          "Failed to load client domains"
        );
      }
      if (partnerDomains.status === "rejected" && !isSessionEnded(partnerDomains.reason)) {
        this.partnerDomainsError = loadErrorMessage(
          partnerDomains.reason,
          "Failed to load partner domains"
        );
      }
      const lookup = [clients, partners].find((r) => r.status === "rejected");
      if (lookup && lookup.status === "rejected" && !isSessionEnded(lookup.reason)) {
        this.lookupError = loadErrorMessage(
          lookup.reason,
          "Failed to load clients and partners"
        );
      }
      this.loading = false;
    });
  }
  async reload() {
    await this.load();
  }

  async addClientDomain(): Promise<void> {
    if (!this.newClientId || !this.newClientDomain.trim()) {
      this.formError = "Pick a client and enter a domain.";
      return;
    }
    this.savingClient = true;
    this.formError = null;
    try {
      // THROW_ON_ERROR on every write here: the ServiceStore otherwise RESOLVES
      // a refusal (and a sign-out) as null, which read as done — and the
      // reload after it went out in whichever session came next.
      await this.clientDomainsService?.create(
        {
          clientId: this.newClientId,
          domain: this.newClientDomain.trim().toLowerCase(),
        } as never,
        THROW_ON_ERROR,
      );
      runInAction(() => {
        this.newClientDomain = "";
      });
      await this.reload();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.formError = err instanceof Error ? err.message : "Failed to add domain";
      });
    } finally {
      runInAction(() => {
        this.savingClient = false;
      });
    }
  }

  async addPartnerDomain(): Promise<void> {
    if (!this.newPartnerId || !this.newPartnerDomain.trim()) {
      this.formError = "Pick a partner and enter a domain.";
      return;
    }
    this.savingPartner = true;
    this.formError = null;
    try {
      await this.partnerDomainsService?.create(
        {
          partnerId: this.newPartnerId,
          domain: this.newPartnerDomain.trim().toLowerCase(),
        } as never,
        THROW_ON_ERROR,
      );
      runInAction(() => {
        this.newPartnerDomain = "";
      });
      await this.reload();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.formError = err instanceof Error ? err.message : "Failed to add domain";
      });
    } finally {
      runInAction(() => {
        this.savingPartner = false;
      });
    }
  }

  /**
   * Report a failed user-initiated action: toast to the user AND a structured
   * `view.action_failed` diagnostic into the retained ring buffer. The two
   * delete paths below used to end in `console.error` — the mapping stayed on
   * screen and the app said nothing at all.
   */
  private reportFailure = createActionReporter(
    () => this.rootStore,
    "DomainMappingsStore",
  );

  async removeClientDomain(id: number) {
    try {
      await this.clientDomainsService?.remove(String(id), THROW_ON_ERROR);
      await this.reload();
    } catch (err) {
      this.reportFailure("remove the client domain mapping", err, {
        data: { id },
      });
    }
  }
  async removePartnerDomain(id: number) {
    try {
      await this.partnerDomainsService?.remove(String(id), THROW_ON_ERROR);
      await this.reload();
    } catch (err) {
      this.reportFailure("remove the partner domain mapping", err, {
        data: { id },
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.clientDomainsError = null;
    this.partnerDomainsError = null;
    this.lookupError = null;
    this.searchQuery = "";
    this.clientSortKey = "domain";
    this.clientSortDir = "asc";
    this.partnerSortKey = "domain";
    this.partnerSortDir = "asc";
    this.newClientId = "";
    this.newClientDomain = "";
    this.newPartnerId = "";
    this.newPartnerDomain = "";
    this.savingClient = false;
    this.savingPartner = false;
    this.formError = null;
  }
}

export default DomainMappingsStore;
